import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Stack,
  Paper,
  Button,
  Tooltip,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import CropIcon from "@mui/icons-material/Crop";
import { baseURLFetch } from "../../../utils/ConstantsURL";
import { useMarkerImage } from "../../../hooks/panelMDM/useMarkerImage";
import { ImageCropModal } from "./ImageCropModal";

export const DEFAULT_MARKER_DURATION = 5;

export const formatTime = (seconds) => {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const rest = safeSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
};

export const parseTime = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 ? Math.floor(value) : null;
  }

  const text = String(value ?? "").trim();

  if (!text) return null;

  if (/^\d+$/.test(text)) {
    return Number(text);
  }

  const match = text.match(/^(\d{1,3}):([0-5]?\d)$/);

  if (!match) return null;

  return Number(match[1]) * 60 + Number(match[2]);
};

export const buildImageUrl = (url) => {
  if (!url) return null;

  return url.startsWith("http") ? url : `${baseURLFetch}${url}`;
};

export const findOverlappingMarker = (
  markers,
  inicio,
  duracion,
  ignoreId = null
) => {
  const fin = inicio + duracion;

  return markers.find(
    (marker) =>
      marker.id !== ignoreId &&
      inicio < marker.inicio + marker.duracion &&
      fin > marker.inicio
  );
};

export const DURATION_MODES = {
  DURACION: "duracion",
  FIN: "fin",
};

export const resolveMarkerDuration = ({
  mode,
  inicio,
  duration,
  endTime,
  audioDuration = 0,
}) => {
  if (mode === DURATION_MODES.FIN) {
    const fin = parseTime(endTime);

    if (fin === null) {
      return {
        segundos: null,
        error: "El momento de fin debe tener el formato mm:ss (por ejemplo 01:40).",
      };
    }

    if (fin <= inicio) {
      return {
        segundos: null,
        error: `El fin (${formatTime(fin)}) debe ser posterior al inicio (${formatTime(
          inicio
        )}).`,
      };
    }

    if (audioDuration && fin > Math.ceil(audioDuration)) {
      return {
        segundos: null,
        error: `El fin excede la duración del audio (${formatTime(audioDuration)}).`,
      };
    }

    return { segundos: fin - inicio, error: null };
  }

  const segundos = Number(duration);

  if (!Number.isFinite(segundos) || segundos <= 0) {
    return {
      segundos: null,
      error: "La duración debe ser un número de segundos mayor a cero.",
    };
  }

  return { segundos: Math.round(segundos), error: null };
};

export const AudioMarkers = ({
  markers = [],
  onChange,
  docId,
  moduleKey,
  currentTime = 0,
  audioDuration = 0,
  onSeek,
  disabled = false,
}) => {
  const { uploadMarkerImage, deleteMarkerImage, loadingImage } =
    useMarkerImage();

  const [startTime, setStartTime] = useState(formatTime(0));
  const [isTimeFocused, setIsTimeFocused] = useState(false);
  const [duration, setDuration] = useState(String(DEFAULT_MARKER_DURATION));
  const [durationMode, setDurationMode] = useState(DURATION_MODES.DURACION);
  const [endTime, setEndTime] = useState(formatTime(DEFAULT_MARKER_DURATION));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [error, setError] = useState("");
  const imageInputRef = useRef(null);

  const isEditing = Boolean(editingId);

  useEffect(() => {
    if (isTimeFocused || editingId) return;

    setStartTime(formatTime(currentTime));
  }, [currentTime, isTimeFocused, editingId]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const sortedMarkers = [...markers].sort((a, b) => a.inicio - b.inicio);

  const clearImageSelection = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    setImageFile(null);
    setImagePreview(null);
    setOriginalFile(null);

    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    clearImageSelection();

    setEditingId(null);
    setCurrentImage(null);
    setDuration(String(DEFAULT_MARKER_DURATION));
    setEndTime(formatTime(Math.floor(currentTime) + DEFAULT_MARKER_DURATION));
    setStartTime(formatTime(currentTime));
  };

  const handleDurationModeChange = (_event, nextMode) => {
    if (!nextMode || nextMode === durationMode) return;

    const inicio = parseTime(startTime);

    if (inicio !== null) {
      if (nextMode === DURATION_MODES.FIN) {
        const segundos = Number(duration);

        if (Number.isFinite(segundos) && segundos > 0) {
          setEndTime(formatTime(inicio + segundos));
        }
      } else {
        const fin = parseTime(endTime);

        if (fin !== null && fin > inicio) {
          setDuration(String(fin - inicio));
        }
      }
    }

    setDurationMode(nextMode);
  };

  const handleSelectImage = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("El archivo seleccionado no es una imagen válida.");
      return;
    }

    setError("");
    setOriginalFile(file);
    setCropOpen(true);
  };

  const handleCropConfirm = (croppedFile) => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    setImageFile(croppedFile);
    setImagePreview(URL.createObjectURL(croppedFile));
    setCropOpen(false);
  };

  const handleCropCancel = () => {
    setCropOpen(false);

    if (!imageFile) {
      setOriginalFile(null);

      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    }
  };

  const handleStartEdit = (marker) => {
    clearImageSelection();

    setEditingId(marker.id);
    setCurrentImage(marker.imagen || null);
    setStartTime(formatTime(marker.inicio));
    setDuration(String(marker.duracion));
    setEndTime(formatTime(marker.inicio + marker.duracion));
    setError("");

    onSeek?.(marker.inicio);
  };

  const handleSubmit = async () => {
    const inicio = parseTime(startTime);

    if (inicio === null) {
      setError("El tiempo debe tener el formato mm:ss (por ejemplo 01:35).");
      return;
    }

    const { segundos, error: duracionError } = resolveMarkerDuration({
      mode: durationMode,
      inicio,
      duration,
      endTime,
      audioDuration,
    });

    if (duracionError) {
      setError(duracionError);
      return;
    }

    if (audioDuration && inicio >= Math.floor(audioDuration)) {
      setError(
        `El marcador inicia fuera del audio (duración total ${formatTime(
          audioDuration
        )}).`
      );
      return;
    }

    if (!imageFile && !isEditing) {
      setError("Selecciona la imagen que se mostrará en pantalla.");
      return;
    }

    const traslape = findOverlappingMarker(
      sortedMarkers,
      inicio,
      segundos,
      editingId
    );

    if (traslape) {
      setError(
        `Esos segundos ya están ocupados por el marcador de ${formatTime(
          traslape.inicio
        )} a ${formatTime(traslape.inicio + traslape.duracion)}.`
      );
      return;
    }

    let imagen = currentImage;

    if (imageFile) {
      const result = await uploadMarkerImage(imageFile, docId, moduleKey);

      if (!result.success) {
        setError(result.message);
        return;
      }

      imagen = result.data;

      if (isEditing && currentImage?.url && currentImage.url !== imagen.url) {
        deleteMarkerImage(currentImage.url);
      }
    }

    const actualizados = isEditing
      ? markers.map((marker) =>
          marker.id === editingId
            ? { ...marker, inicio, duracion: segundos, imagen }
            : marker
        )
      : [
          ...markers,
          {
            id: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
            inicio,
            duracion: segundos,
            imagen,
          },
        ];

    onChange(actualizados.sort((a, b) => a.inicio - b.inicio));

    setError("");
    resetForm();
  };

  const handleDeleteMarker = (marker) => {
    onChange(markers.filter((item) => item.id !== marker.id));

    if (marker.imagen?.url) {
      deleteMarkerImage(marker.imagen.url);
    }

    if (marker.id === editingId) {
      resetForm();
    }
  };

  const isBusy = disabled || loadingImage;
  const previewSrc = imagePreview || buildImageUrl(currentImage?.url);

  const rangoCalculado = (() => {
    const inicio = parseTime(startTime);

    if (inicio === null) return null;

    const { segundos } = resolveMarkerDuration({
      mode: durationMode,
      inicio,
      duration,
      endTime,
      audioDuration,
    });

    return segundos === null ? null : { inicio, segundos };
  })();

  const imageButtonLabel = imageFile
    ? imageFile.name
    : isEditing
      ? "Cambiar imagen"
      : "Seleccionar imagen";

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 3,
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        textAlign: "left",
        borderColor: isEditing ? "#2563eb" : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <MyLocationIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={600}>
          Marcadores de imagen
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({sortedMarkers.length})
        </Typography>
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 2 }}
      >
        Mueve el cursor del audio al momento deseado y define el final del
        marcador: por los segundos que la imagen estará en pantalla o indicando
        el minuto exacto en que termina. Al elegir una imagen se abre el editor
        para recortarla en formato 16:9. Dos marcadores no pueden ocupar los
        mismos segundos.
      </Typography>

      {isEditing && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={resetForm}>
              Cancelar
            </Button>
          }
        >
          Editando el marcador de {formatTime(parseTime(startTime) ?? 0)}. Si no
          cambias la imagen se conservará la actual.
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Typography variant="caption" color="text.secondary">
          Definir el final del marcador por:
        </Typography>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={durationMode}
          onChange={handleDurationModeChange}
          disabled={isBusy}
        >
          <ToggleButton
            value={DURATION_MODES.DURACION}
            sx={{ textTransform: "none", px: 1.5 }}
          >
            Duración
          </ToggleButton>

          <ToggleButton
            value={DURATION_MODES.FIN}
            sx={{ textTransform: "none", px: 1.5 }}
          >
            Momento de fin
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "flex-start" }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Minuto y segundo del audio donde inicia el marcador">
            <TextField
              label="Momento del audio"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              onFocus={() => setIsTimeFocused(true)}
              onBlur={() => setIsTimeFocused(false)}
              disabled={isBusy}
              placeholder="mm:ss"
              sx={{ minWidth: { md: 160 } }}
            />
          </Tooltip>

          <Tooltip title="Usar la posición actual del audio">
            <span>
              <IconButton
                color="primary"
                disabled={isBusy}
                onClick={() => setStartTime(formatTime(currentTime))}
              >
                <GpsFixedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {durationMode === DURATION_MODES.DURACION ? (
          <Tooltip title="Segundos que la imagen permanecerá en pantalla">
            <TextField
              label="Duración (seg)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              disabled={isBusy}
              inputProps={{ min: 1, step: 1 }}
              sx={{ minWidth: { md: 140 } }}
            />
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Minuto y segundo en que la imagen deja de mostrarse">
              <TextField
                label="Momento de fin"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={isBusy}
                placeholder="mm:ss"
                sx={{ minWidth: { md: 160 } }}
              />
            </Tooltip>

            <Tooltip title="Usar la posición actual del audio">
              <span>
                <IconButton
                  color="primary"
                  disabled={isBusy}
                  onClick={() => setEndTime(formatTime(currentTime))}
                >
                  <GpsFixedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            disabled={isBusy}
            onChange={(e) => handleSelectImage(e.target.files?.[0])}
          />

          <Button
            fullWidth
            variant="outlined"
            startIcon={<ImageIcon />}
            disabled={isBusy}
            onClick={() => imageInputRef.current?.click()}
            sx={{ height: 56, justifyContent: "flex-start", overflow: "hidden" }}
          >
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {imageButtonLabel}
            </Box>
          </Button>

          {previewSrc && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <Box
                component="img"
                src={previewSrc}
                alt="Vista previa del marcador"
                sx={{
                  width: 100,
                  height: 56,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "1px solid #e2e8f0",
                }}
              />

              <Button
                size="small"
                startIcon={<CropIcon />}
                disabled={isBusy || !originalFile}
                onClick={() => setCropOpen(true)}
              >
                Reajustar recorte
              </Button>
            </Stack>
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={
              loadingImage ? (
                <CircularProgress size={18} />
              ) : isEditing ? (
                <SaveIcon />
              ) : (
                <AddIcon />
              )
            }
            disabled={isBusy}
            onClick={handleSubmit}
            sx={{ height: 56, px: 3, whiteSpace: "nowrap" }}
          >
            {loadingImage
              ? "Subiendo..."
              : isEditing
                ? "Guardar cambios"
                : "Agregar"}
          </Button>

          {isEditing && (
            <Tooltip title="Cancelar edición">
              <span>
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={isBusy}
                  onClick={resetForm}
                  sx={{ height: 56, minWidth: 56, px: 0 }}
                >
                  <CloseIcon />
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {rangoCalculado && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1.5 }}
        >
          El marcador ocupará de{" "}
          <strong>{formatTime(rangoCalculado.inicio)}</strong> a{" "}
          <strong>
            {formatTime(rangoCalculado.inicio + rangoCalculado.segundos)}
          </strong>{" "}
          ({rangoCalculado.segundos}s en pantalla).
        </Typography>
      )}

      <Box sx={{ mt: 3 }}>
        {sortedMarkers.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 2 }}
          >
            Aún no hay marcadores para este audio.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {sortedMarkers.map((marker) => {
              const isRowEditing = marker.id === editingId;

              return (
                <Stack
                  key={marker.id}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    border: isRowEditing
                      ? "2px solid #2563eb"
                      : "1px solid #e2e8f0",
                    backgroundColor: isRowEditing ? "#eff6ff" : "#f8fafc",
                  }}
                >
                  <Box
                    component="img"
                    src={buildImageUrl(marker.imagen?.url)}
                    alt={marker.imagen?.nombre || "Marcador"}
                    sx={{
                      width: 92,
                      height: 52,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: "1px solid #e2e8f0",
                      backgroundColor: "#fff",
                      flexShrink: 0,
                    }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {formatTime(marker.inicio)} →{" "}
                      {formatTime(marker.inicio + marker.duracion)}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({marker.duracion}s en pantalla)
                      </Typography>
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {marker.imagen?.nombre || "Sin nombre"}
                    </Typography>
                  </Box>

                  <Tooltip title="Ir a este momento del audio">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={!onSeek}
                        onClick={() => onSeek?.(marker.inicio)}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={
                      isRowEditing ? "Cancelar edición" : "Editar marcador"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color={isRowEditing ? "warning" : "default"}
                        disabled={isBusy}
                        onClick={() =>
                          isRowEditing ? resetForm() : handleStartEdit(marker)
                        }
                      >
                        {isRowEditing ? <CloseIcon /> : <EditIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Eliminar marcador">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={isBusy}
                        onClick={() => handleDeleteMarker(marker)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>

      <ImageCropModal
        open={cropOpen}
        file={originalFile}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
        title="Recortar imagen del marcador"
      />
    </Paper>
  );
};
